/**
 * UsernameAPI - Username validation and management
 *
 * Usernames are optional and used for URL prefixes:
 * xrgallery.online/username/tour-name
 */

import {
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('UsernameAPI');

/**
 * Reserved usernames that cannot be used
 */
export const RESERVED_USERNAMES: string[] = [
  // System/admin
  'admin',
  'administrator',
  'root',
  'system',
  'support',
  'help',
  'info',
  'contact',
  'sales',
  'team',
  'staff',
  'moderator',
  'mod',

  // Brand protection
  'xrgallery',
  'webxr',
  'official',
  'verified',
  'anthropic',
  'claude',

  // Common reserved
  'api',
  'app',
  'www',
  'web',
  'mail',
  'email',
  'ftp',
  'cdn',
  'static',
  'assets',
  'blog',
  'news',
  'store',
  'shop',

  // Offensive/problematic (add more as needed)
  'null',
  'undefined',
  'anonymous',
  'unknown',
  'guest',
  'test',
  'testing',
  'demo',
  'example',

  // App routes that could conflict
  'dashboard',
  'settings',
  'account',
  'profile',
  'login',
  'logout',
  'signup',
  'register',
  'auth',
  'oauth',
  'callback',
  'discover',
  'explore',
  'search',
  'tour',
  'tours',
  'gallery',
  'galleries',
  'pricing',
  'plans',
  'billing',
  'upgrade',
  'subscribe',
  'privacy',
  'terms',
  'legal',
  'docs',
  'documentation',
  'newsletter',
];

/**
 * Username validation result
 */
export interface UsernameValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a username for format compliance
 *
 * Rules:
 * - Length: 3-30 characters
 * - Characters: lowercase a-z, 0-9, underscores, hyphens
 * - Must start and end with alphanumeric
 * - Not in reserved list
 */
export function validateUsername(username: string): UsernameValidationResult {
  // Trim and lowercase
  const normalizedUsername = username.trim().toLowerCase();

  // Length check
  if (normalizedUsername.length < 3) {
    return { valid: false, error: 'Username must be at least 3 characters' };
  }
  if (normalizedUsername.length > 30) {
    return { valid: false, error: 'Username must be 30 characters or less' };
  }

  // Character check (a-z, 0-9, underscores, hyphens only)
  if (!/^[a-z0-9_-]+$/.test(normalizedUsername)) {
    return { valid: false, error: 'Username can only contain lowercase letters, numbers, underscores, and hyphens' };
  }

  // Must start with letter or number
  if (!/^[a-z0-9]/.test(normalizedUsername)) {
    return { valid: false, error: 'Username must start with a letter or number' };
  }

  // Must end with letter or number
  if (!/[a-z0-9]$/.test(normalizedUsername)) {
    return { valid: false, error: 'Username must end with a letter or number' };
  }

  // No consecutive underscores or hyphens
  if (/__|--/.test(normalizedUsername)) {
    return { valid: false, error: 'Username cannot contain consecutive underscores or hyphens' };
  }

  // Reserved username check
  if (RESERVED_USERNAMES.includes(normalizedUsername)) {
    return { valid: false, error: 'This username is reserved and cannot be used' };
  }

  return { valid: true };
}

/**
 * Check if a username is available
 * Returns true if the username is not taken
 */
export async function checkUsernameAvailability(username: string): Promise<boolean> {
  try {
    const normalizedUsername = username.trim().toLowerCase();

    // First validate format
    const validation = validateUsername(normalizedUsername);
    if (!validation.valid) {
      return false;
    }

    // Check the usernames collection
    const usernameRef = doc(db, 'usernames', normalizedUsername);
    const usernameDoc = await getDoc(usernameRef);

    return !usernameDoc.exists();
  } catch (error) {
    logger.error('Error checking username availability:', error);
    throw new Error('Failed to check username availability');
  }
}

/**
 * Get userId by username
 */
export async function getUserIdByUsername(username: string): Promise<string | null> {
  try {
    const normalizedUsername = username.trim().toLowerCase();
    const usernameRef = doc(db, 'usernames', normalizedUsername);
    const usernameDoc = await getDoc(usernameRef);

    if (!usernameDoc.exists()) {
      return null;
    }

    return usernameDoc.data().userId;
  } catch (error) {
    logger.error('Error getting userId by username:', error);
    return null;
  }
}

/**
 * Get username by userId
 * Queries the users collection for the username field
 */
export async function getUsernameByUserId(userId: string): Promise<string | null> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    const data = userDoc.data();
    return data.username || null;
  } catch (error) {
    logger.error('Error getting username by userId:', error);
    return null;
  }
}

/**
 * Normalize a username (trim and lowercase)
 */
export function normalizeUsername(username: string): string {
  return username.trim().toLowerCase();
}

/**
 * Generate a suggested username from display name or email
 *
 * Examples:
 * - "John Smith" -> "john-smith"
 * - "john@example.com" -> "john"
 */
export function generateUsernameFromName(name: string): string {
  // If it's an email, take the part before @
  if (name.includes('@')) {
    name = name.split('@')[0];
  }

  return name
    .toLowerCase()
    .trim()
    // Replace spaces with hyphens
    .replace(/\s+/g, '-')
    // Remove any non-allowed characters
    .replace(/[^a-z0-9_-]/g, '')
    // Remove consecutive hyphens/underscores
    .replace(/[-_]+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^[-_]|[-_]$/g, '')
    // Ensure minimum length
    .substring(0, 30);
}
